import React, {useState} from 'react'
import moment from 'moment'
import {Typography} from '@mui/material'
import {makeStyles} from '@mui/styles'
import PropTypes from 'prop-types'
import {bodyText, H4HeaderBlack, subBodyText, totalText} from '../../../assets/styles/typography'
import {spaceExtraLarge, spaceMedium, spaceSmall} from '../../../assets/styles/spacing'
import {rxrBlackColor, rxrWhiteColor, rxrPrimaryColor, rxrRedColor} from '../../../assets/styles/color'
import ChargesTable from './ChargesTable'
import PaymentsTable from './PaymentsTable'
import RXRIcon from '../../RXRIcon'
import RXRButton from '../../RXRButton'
import ConfirmDeleteDialog from '../../ConfirmDeleteDialog'
import {INVOICE_STATUS_PARTIAL, INVOICE_STATUS_PARTIAL_LATE} from '../../../constants/ModelConstants'
import MessageBox from '../../MessageBox'
import {useSelector} from 'react-redux'

const useStyles = makeStyles(theme => ({
  parentContainer: {
    flexGrow: 1,
  },
  rowContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  totalText: {
    ...totalText,
  },
  H4HeaderBlack: {
    ...H4HeaderBlack,
  },
  sectionContainer: {
    paddingBottom: spaceExtraLarge,
  },
  bodyText: {...bodyText},
  subBodyText: {...subBodyText, marginTop: spaceSmall},
  balance: {
    fontWeight: 'bold',
    fontSize: 16,
    color: rxrBlackColor,
    fontStyle: 'normal',
  },

  messageContainer: {
    marginBottom: spaceMedium,
  },
}))

export default function RentBreakdown(props) {
  const classes = useStyles()
  const [showRefreshDialog, setShowRefreshDialog] = useState(false)
  const [hasRefreshedInvoice, setHasRefreshedInvoice] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const occupancy = useSelector(state => state.Occupancies.occupanciesLookup[props.occupancyId])

  const handleClickRefreshInvoice = () => {
    if (!showRefreshDialog) {
      setShowRefreshDialog(true)
      return
    }
    setIsRefreshing(true)
    setShowRefreshDialog(false)
    props
      .onRefreshInvoice()
      .then(() => {
        setHasRefreshedInvoice(true)
      })
      .finally(() => {
        setIsRefreshing(true)
      })
  }

  const canRefresh =
    !hasRefreshedInvoice &&
    props.activeInvoice &&
    [INVOICE_STATUS_PARTIAL, INVOICE_STATUS_PARTIAL_LATE].includes(props.activeInvoice.status)

  return (
    <div className={classes.parentContainer}>
      <React.Fragment>
        {occupancy.isRentSuspended ? (
          <MessageBox className={classes.messageContainer} icon={RXRIcon.ERROR} color={rxrRedColor}>
            Resident is in a <strong>Do Not Accept</strong> state, and will not be able to pay rent in the RXO Home app.
          </MessageBox>
        ) : null}

        <div className={classes.sectionContainer}>
          {props.activeInvoice ? (
            <React.Fragment>
              <div style={{marginBottom: spaceMedium}}>
                <PaymentsTable payments={props.activeInvoice.payments} />
              </div>
              <div style={{marginBottom: spaceMedium}}>
                <div className={classes.rowContainer}>
                  <Typography className={classes.balance}>Balance</Typography>
                  <Typography className={classes.balance}>
                    {`$${(props.activeInvoice.total - props.activeInvoice.amountPaid).toFixed(2)}`}
                  </Typography>
                </div>
                <div className={classes.rowContainer}>
                  <Typography className={classes.bodyText}>
                    Paid ${props.activeInvoice.amountPaid} / ${props.activeInvoice.total}
                  </Typography>
                  <Typography className={classes.bodyText}>{`Due at ${moment(props.activeInvoice.dueAt).format(
                    'MMM D, YYYY',
                  )}`}</Typography>
                </div>
              </div>
              <div className={classes.rowContainer}>
                <div>
                  <RXRButton disabled={!canRefresh} onClick={handleClickRefreshInvoice} isLoading={isRefreshing}>
                    Re-sync balance
                  </RXRButton>

                  {!canRefresh && (
                    <Typography className={classes.subBodyText}>
                      Invoices can only be refreshed if they're in a partially paid state
                    </Typography>
                  )}
                </div>
              </div>
              <ConfirmDeleteDialog
                isOpen={showRefreshDialog}
                onCancel={() => setShowRefreshDialog(false)}
                onConfirmDelete={handleClickRefreshInvoice}
                prompt={
                  "Once you refresh this invoice, it may take a several hours for the balance to update in the RXO Home mobile app. During this time, the resident will not be able to make additional payments. Are you sure you'd like to proceed?"
                }
                deleteButtonText={'Re-sync balance'}
              />
            </React.Fragment>
          ) : (
            <Typography>Resident does not currently have an outstanding balance</Typography>
          )}
        </div>

        <ChargesTable occupancyId={props.occupancyId} />
      </React.Fragment>
    </div>
  )
}

RentBreakdown.propTypes = {
  occupancyId: PropTypes.string.isRequired,
  activeInvoice: PropTypes.object,
  onRefreshInvoice: PropTypes.func,
}
